import { ArrowPointerService } from './arrow-pointer.service';
import { customAssetInfoTooltipFormatter } from './tooltip-formatter';

export class TooltipService {
    /**
     * Performs check is the custom tooltip available and applies proper tooltip based on the type
     * @param {EChartsType} chartInstance
     * @param {HTMLDivElement} element
     * @returns null
     */
    applyCustomTooltip(chartInstance, element) {
        const configuration = chartInstance.getOption();
        let tooltipApplied = false;
        configuration.series.forEach((el) => {
            if (this.shouldApplyCustomTooltipToSeries(el) && !tooltipApplied) {
                this.applyCustomAssetInfoTooltip(chartInstance);
                tooltipApplied = true;
                return;
            }
        });
        const showCustomTooltip = configuration.tooltip[0]?.customTooltip?.show;
        if (!showCustomTooltip) {
            return;
        }

        switch (configuration.tooltip[0]?.customTooltip?.type) {
            case 'pathways':
                this.applyCustomPathwaysTooltip(chartInstance);
                break;
            case 'spark_line':
                this.applyCustomSparkLineTooltip(chartInstance, element);
                break;
            case 'measures_overview':
                this.applyCustomMeasuresOverviewTooltip(chartInstance, element);
                break;
            case 'month_over_month_timeline':
                this.applyCustomMonthOverMonthTimelineTooltip(chartInstance, element);
                break;
            case 'month_over_month_histogram':
                this.applyCustomMonthOverMonthHistogramTooltip(chartInstance, element);
                break;
        }
    }

    shouldApplyCustomTooltipToSeries(singleSeries) {
        if (
            singleSeries.tooltip?.customTooltip?.show &&
            singleSeries.tooltip?.customTooltip?.type == 'asset_info' &&
            singleSeries.data[0] &&
            Object.keys(singleSeries.data[0].tooltipData?.assetData ?? {}).length !== 0
        ) {
            return true;
        }
        return false;
    }

    /**
     * @param {EChartsType} chart - chart instance
     * @param {HTMLDivElement} element
     * @returns null
     */
    applyCustomAssetInfoTooltip(chart) {
        const chartOptions = chart.getOption();
        let series = [];
        let tooltipOptions = {};
        chartOptions.series.forEach((el) => {
            if (el.tooltip?.customTooltip?.type !== 'asset_info') {
                series.push(el);
                return;
            }

            let showBasicInfo = el.tooltip?.customTooltip?.showBasicInfo;
            let showBasicInfoV2 = el.tooltip?.customTooltip?.showBasicInfoV2;

            tooltipOptions = {
                tooltip: {
                    trigger: chartOptions.series[0].type === 'bar' && !showBasicInfo && !showBasicInfoV2 ? 'axis' : 'item',
                    enterable: !showBasicInfoV2,
                    axisPointer: {
                        type: 'shadow',
                    },
                    padding: showBasicInfo ? 20 : (showBasicInfoV2 ? 14 : 0),
                    backgroundColor: !showBasicInfoV2 ? 'rgba(0, 0, 0, 0)' : 'white',
                    extraCssText: !showBasicInfoV2 ? 'box-shadow: 0 0 3px rgba(0, 0, 0, 0);' : '',
                    borderWidth: 0,
                    position: showBasicInfo || showBasicInfoV2 ? 'top' : function (point, params, dom, rect, size) {
                        let tooltipWidth = dom.getBoundingClientRect().width;
                        let tooltipHeight = dom.getBoundingClientRect().height;
                        let xPosition = point[0] - tooltipWidth / 2;
                        let yPosition = point[1] - tooltipHeight - 6;
                        // Keep tooltip inside width of the chart container so the horizontal scroll is not shown
                        // when user selects last item.
                        if (xPosition + tooltipWidth > size.viewSize[0]) {
                            xPosition = xPosition - (xPosition + tooltipWidth - size.viewSize[0]);
                        }
                        xPosition = xPosition < 0 ? 0 : xPosition;

                        return [xPosition, yPosition];
                    },
                    formatter: customAssetInfoTooltipFormatter.bind(chart),
                },
            };
            series.push({ ...el, ...tooltipOptions });
        });

        chart.setOption({
            series: series,
            tooltip:
                chartOptions.series[0].type === 'bar' ? tooltipOptions.tooltip : { trigger: 'item', enterable: true },
        });
    }

    /**
     * This function adds arrow pointer on top of the chart, it is handled with tooltip so the pointer can change
     * by hovering axis
     * @param {EChartsType} chartInstance
     * @param {HTMLDivElement} element
     * @returns null
     */
    applyCustomSparkLineTooltip(chartInstance, element) {
        let tooltipOptions = {
            trigger: 'axis',
            formatter: function (params) {
                const chartOptions = chartInstance.getOption();
                const currentValue = params[0].value !== undefined ? params[0].value : null;
                if (chartOptions.customArrowPointer.version == 2) {
                    const color = ArrowPointerService.prototype.getTextColorBasedOnPercentage(currentValue);
                    setTimeout(() => {
                        chartInstance.setOption({ 'emphasis': { itemStyle: { borderColor: color, borderWidth: 1.5 } } });
                    }, 100);
                    ArrowPointerService.prototype.appendCurrentYearDetails(element, chartOptions, currentValue, chartOptions.xAxis[0].data[params[0].dataIndex], color);
                } else {
                    const percentageDiff = ArrowPointerService.prototype.calculateYearOverYearPercentage(
                        currentValue,
                        chartOptions.series[0]['data'][params[0].dataIndex - 1] ?? null
                    );
                    ArrowPointerService.prototype.appendArrowPointer(element, chartOptions, currentValue, percentageDiff);
                }
                // This leaves circle on currently selected axis after navigating away from the chart.
                setTimeout(() => {
                    chartInstance.dispatchAction({
                        type: 'downplay',
                    });
                    chartInstance.dispatchAction({
                        type: 'highlight',
                        dataIndex: params[0].dataIndex,
                    });
                }, 50);

                return ``;
            },
        };

        chartInstance.setOption({ tooltip: tooltipOptions });
    }

    /**
     * @param {EChartsType} chartInstance
     * @returns null
     */
    applyCustomPathwaysTooltip(chartInstance) {
        const chartOptions = chartInstance.getOption();
        const self = this;
        const isDarkLayout = chartOptions.tooltip[0].customTooltip.layout == 'dark';
        let tooltipOptions = {
            trigger: 'axis',
            axisPointer: chartOptions.tooltip[0].customTooltip.hideAxisHover ? {
                type: 'none',
            } : {},
            formatter: function (params) {
                let tooltipSeries = [];
                let total = 0;
                const chartOptions = chartInstance.getOption();
                if (chartOptions.useAllSeriesForTooltip) {
                    chartOptions.series.forEach((el, index) => {
                        if (!el.tooltip?.hideFromTooltip && el.data[params[0].dataIndex] !== null && !tooltipSeries.find((item) => item.name === el.name)) {
                            let value, tooltip;
                            if (typeof el.data[params[0].dataIndex] === 'object') {
                                value = el.data[params[0].dataIndex].value;
                                tooltip = el.data[params[0].dataIndex].tooltip;
                            } else {
                                value = el.data[params[0].dataIndex];
                            }
                            if (value && (!el.isDataPendingSeries || el.data[params[0].dataIndex]?.isDataPendingItem)) {
                                total += +value;
                                tooltipSeries.push({
                                    color: el.color ?? (chartOptions.color ? chartOptions.color[index] : ''),
                                    name: el.name,
                                    value: value,
                                    tooltip: tooltip,
                                });
                            }
                        }
                    });
                } else {
                    // When user hovers directly over data pending item
                    if (params.length === undefined && chartOptions.series[params.seriesIndex].isDataPendingSeries) {
                        return chartOptions.series[params.seriesIndex].title
                    }
                    params.forEach((el) => {
                        if (el.data !== null && !tooltipSeries.find((item) => item.name === el.seriesName)) {
                            let value, tooltip;
                            if (typeof el.data === 'object') {
                                value = el.data.value;
                                tooltip = el.data.tooltip;
                            } else {
                                value = el.data;
                            }

                            if (value) {
                                total += +value;
                                tooltipSeries.push({
                                    color: el.color,
                                    name: el.seriesName,
                                    value: value,
                                    tooltip: tooltip,
                                });
                            }
                        }
                    });
                }
                if (tooltipSeries.length === 0) {
                    return '';
                }
                total = chartOptions.tooltip[0].customTooltip.showTotalSum ? total.toFixed(2) : '';
                if (isDarkLayout) {
                    return self.getPathwaysDarkLayoutTooltipContent(chartOptions, tooltipSeries, total);
                }

                let tooltipContent = chartOptions.tooltip[0].customTooltip.title
                    ? `<span style="font-weight:bold;">${chartOptions.tooltip[0].customTooltip.title} ${total}</span><br>`
                    : '';
                tooltipSeries = chartOptions.tooltip[0].customTooltip.reverseSeries ? tooltipSeries.reverse() : tooltipSeries;
                tooltipSeries.forEach((el) => {
                    tooltipContent += `
                        <div style="display:inline-flex; align-items: center;">
                                <div style="height: 10px; width: 10px; border-radius: 10px; background:${el.color
                        }; margin: 5px 5px 5px 0;"></div>
                                ${el.name}:<span style="font-weight:bold;margin-left:3px;">${el.tooltip ?? el.value
                        }</span>
                        </div><br>
                    `;
                });

                return tooltipContent;
            },
        };
        if (isDarkLayout) {
            // This removes default tooltip borders, background and padding
            tooltipOptions.padding = 0;
            tooltipOptions.backgroundColor = 'rgba(0, 0, 0, 0)';
            tooltipOptions.borderWidth = 0;
        }
        chartInstance.setOption({ tooltip: tooltipOptions });
    }

    /**
     * @param chartOptions - Chart configuration.
     * @param tooltipSeries - All available chart dataset series.
     * @param total - Sum of all series values.
     *
     * @returns string
     */
    getPathwaysDarkLayoutTooltipContent(chartOptions, tooltipSeries, total) {
        let tooltipContent = `
            <div style="
                background: rgba(0, 0, 0, 0.75);
                border-radius: 10px;
                color: rgba(255, 255, 255, 1);
                font-size: 11px;
                font-weight: bold;
                padding: 12px 15px 12px 15px;
                min-width: 169px;
                margin: -3px -3px -3px -3px;
            ">
            <table style="font-size: 10px;line-height: 14px;">`;

        const unit = chartOptions.tooltip[0].customTooltip.unit ?? '';
        tooltipContent += chartOptions.tooltip[0].customTooltip.title
            ? `<tr>
                <td style="font-weight: 700;">${chartOptions.tooltip[0].customTooltip.title}</td>
                <td>${total}${unit}</td>
            </tr>`
            : '';
        tooltipSeries = tooltipSeries.reverse();
        tooltipSeries.forEach((el) => {
            tooltipContent += `<tr>
                <td style="color: rgba(176, 176, 176, 1);">${el.name}:</td>
                <td>${el.tooltip ?? el.value}${unit}</td>
            </tr>`;
        });

        return (tooltipContent += '</table></div>');
    }

    /**
     * @param chartOptions - Chart configuration.
     * @param tooltipSeries - All available chart dataset series.
     * @param total - Sum of all series values.
     *
     * @returns string
     */
    getPathwaysLightLayoutTooltipContent(chartOptions, tooltipSeries, total) {
        let tooltipContent = chartOptions.tooltip[0].customTooltip.title
            ? `<span style="font-weight:bold;">${chartOptions.tooltip[0].customTooltip.title} ${total}</span><br>`
            : '';
        tooltipSeries.forEach((el) => {
            tooltipContent += `
                <div style="display:inline-flex; align-items: center;">
                        <div style="height: 10px; width: 10px; border-radius: 10px; background:${el.color
                }; margin: 5px 5px 5px 0;"></div>
                        ${el.name}:<span style="font-weight:bold;margin-left:3px;">${el.tooltip ?? el.value}</span>
                </div><br>
            `;
        });

        return tooltipContent;
    }

    /**
     * @param {EChartsType} chartInstance
     * @returns null
     */
    applyCustomMeasuresOverviewTooltip(chartInstance) {
        const tooltipOptions = {
            trigger: 'item',
            padding: 12,
            extraCssText: 'border-radius: 8px;',
            formatter: function (params) {
                let items = ``;
                if (params.data?.tooltip) {
                    for (let [key, value] of Object.entries(params.data?.tooltip)) {
                        items += `<div><span style="color: #99A4B1;">${key}: </span><span style="color: #111827;">${value > 999 ? value/1000 + 'M' : value + 'k'}</span></div>`;
                    }
                }
                return `
                    <div style="font-size: 10px; font-weight: 700;">
                        <div><span style="color: #99A4B1;">Measure group: </span><span style="color: #111827;">${params.seriesName}</span></div>
                        <div><span style="color: #99A4B1;">Total investment: </span><span style="color: #111827;">${params.data.value > 999 ? params.data.value/1000 + 'M' : params.data.value + 'k'}</span></div>
                        <div style="height: 1px; width: 100%; margin-top: 8px; margin-bottom: 8px; background: ${params.color};"></div>
                        ${items}
                    </div>
                `;
            },
        };
        const yAxis = {
            ...chartInstance.getOption().yAxis,
        }
        if (!yAxis.axisLabel) {
            yAxis.axisLabel = {};
        }
        yAxis.axisLabel.formatter = (value) => {
            return value > 999 ? value/1000 + 'M' : value + 'k';
        }
        chartInstance.setOption({ tooltip: tooltipOptions, yAxis: yAxis });
    }

    /**
     * @param {EChartsType} chartInstance
     * @returns null
     */
    applyCustomMonthOverMonthTimelineTooltip(chartInstance) {
        const chartOptions = chartInstance.getOption();
        const tooltipOptions = {
            trigger: 'axis',
            padding: 12,
            extraCssText: 'border-radius: 8px;',
            formatter: function (params) {
                let tooltipSeries = [];
                let total = 0;
                params.forEach((el) => {
                    if (el.data !== null && !tooltipSeries.find((item) => item.name === el.seriesName)) {
                        let value, tooltip;
                        if (typeof el.data === 'object') {
                            value = el.data.value;
                            tooltip = el.data.tooltip;
                        } else {
                            value = el.data;
                        }
                        if (value) {
                            total += +value;
                            tooltipSeries.push({
                                color: el.color,
                                name: `${el.axisValue} ${el.data.tooltip.tooltip_year ?? el.seriesName}`,
                                value: value,
                                tooltip: tooltip,
                            });
                        }
                    }
                });

                if (tooltipSeries.length === 0) {
                    return '';
                }
                total = chartOptions.tooltip[0].customTooltip.showTotalSum ? total.toFixed(2) : '';

                let tooltipContent = chartOptions.tooltip[0].customTooltip.title
                    ? `<span style="font-weight:bold;color:#000;font-size:14px;">${chartOptions.tooltip[0].customTooltip.title} ${total}</span><br><span style="font-size:10px;font-weight:bold;color:#99A4B1;">${chartOptions.tooltip[0].customTooltip.unit}</span><br>`
                    : '';
                tooltipSeries = chartOptions.tooltip[0].customTooltip.reverseSeries ? tooltipSeries.reverse() : tooltipSeries;
                tooltipSeries.forEach((el) => {
                    tooltipContent += `
                        <div style="display:inline-flex; align-items: center; justify-content: between; width: 100%;">
                                <div style="height: 28px; width: 4px; border-radius: 10px; background:${el.color
                        }; margin-right: 10px;"></div>
                               <span style="color: #99A4B1;font-weight:bold;font-size:14px;">${el.name}</span><span style="font-size:14px;font-weight:bold;color:#000;margin-left:auto;">${el.value}</span>
                        </div><br>
                    `;
                    if (el.tooltip.estimated_percentage) {
                        tooltipContent += `
                            <div style="display:inline-flex; align-items: center; justify-content: between; width: 100%;">
                                    <div style="height: 28px; width: 4px; border-radius: 10px; background:${el.color
                            }; margin-right: 10px;"></div>
                                <span style="display:block;margin-right: 12px; color: #99A4B1;font-weight:bold;font-size:14px;">Estimated data</span><span style="font-size:14px;font-weight:bold;color:#000;margin-left:auto;">${el.tooltip.estimated_percentage}%</span>
                            </div><br>
                        `;
                    }
                });

                return tooltipContent;
            },
        };
        chartInstance.setOption({ tooltip: tooltipOptions });
    }

    /**
     * @param {EChartsType} chartInstance
     * @returns null
     */
    applyCustomMonthOverMonthHistogramTooltip(chartInstance) {
        const chartOptions = chartInstance.getOption();
        const tooltipOptions = {
            trigger: 'axis',
            padding: 12,
            confine: true,
            extraCssText: 'border-radius: 8px;',
            formatter: function (params) {
                let tooltipSeries = [];
                let seriesPerYears = [];
                params.forEach(element => {
                    const nameParts = element.seriesName.split('|').length > 1 ? element.seriesName.split('|') : element.seriesName.split(' ');
                    const lastIndex = nameParts.length - 1;
                    seriesPerYears[nameParts[lastIndex]] = [];
                });
                let total = 0;
                params.forEach((el) => {
                    if (el.data !== null && !tooltipSeries.find((item) => item.name === el.seriesName)) {
                        let value, tooltip;
                        if (typeof el.data === 'object') {
                            value = el.data.value;
                            tooltip = el.data.tooltip;
                        } else {
                            value = el.data;
                        }
                        if (value) {
                            total += +value;
                            const nameParts = el.seriesName.split('|').length > 1 ? el.seriesName.split('|') : el.seriesName.split(' ');
                            const lastIndex = nameParts.length - 1;
                            const isEstimated = el.seriesName.includes('estimated');
                            const seriesName = nameParts[0] + ' - ' +  nameParts[lastIndex];
                            seriesPerYears[nameParts[lastIndex]].push({
                                color: el.data?.itemStyle?.mainColor ?? el.color,
                                isEstimated: isEstimated,
                                name: seriesName,
                                title: isEstimated ? 'Estimated data' : seriesName,
                                xAxis: el.name,
                                value: value,
                                tooltip: tooltip,
                            });
                        }
                    }
                });
                total = chartOptions.tooltip[0].customTooltip.showTotalSum ? total.toFixed(2) : '';
                let tooltipContent = chartOptions.tooltip[0].customTooltip.title
                    ? `<span style="font-weight:bold;color:#000;font-size:14px; min-width: 193px;">${chartOptions.tooltip[0].customTooltip.title} ${total}</span><br><span style="font-size:10px;font-weight:bold;color:#99A4B1;">${chartOptions.tooltip[0].customTooltip.unit}</span><br>`
                    : '';
                seriesPerYears.forEach((seriesPerYear, year) => {
                    if(seriesPerYear.length) {
                        tooltipContent += `
                            <div style="display:inline-flex; align-items: center; justify-content: between; width: 100%;">
                                <div style="height: 24px; width: 3px;border-top-left-radius: 10px;border-top-right-radius: 10px; background:${seriesPerYear[0].color}; margin-right: 10px;"></div>
                                <span style="font-size:10px;font-weight: bold;color: #99A4B1;">${seriesPerYear[0].xAxis} ${seriesPerYear[0].tooltip?.tooltip_year ?? year}</span>
                            </div><br>
                        `;
                        let total = 0;
                        seriesPerYear.forEach((el) => {
                            if (!el.isEstimated) {
                                const estimatedSerie = seriesPerYear.filter((item) => item.isEstimated && item.name === el.name)[0];
                                const itemValue = estimatedSerie?.value
                                    ? Math.round((parseFloat(estimatedSerie.value) + parseFloat(el.value)) * 100) / 100
                                    : el.value;
                                tooltipContent += `
                                    <div style="display:inline-flex; align-items: center; justify-content: between; width: 100%;margin-top: -8px;">
                                        <div style="height: 24px; width: 3px; background:${seriesPerYear[0].color}; margin-right: 10px;"></div>
                                        <span style="color: #111827;font-weight:bold;font-size:14px;margin-right: 30px;">${el.name}</span><span style="font-size:14px;font-weight:bold;color:#000;margin-left:auto;">${itemValue}</span>
                                    </div><br>
                                `;
                                if (estimatedSerie) {
                                    tooltipContent += `
                                        <div style="display:inline-flex; align-items: center; justify-content: between; width: 100%;margin-top: -8px;">
                                            <div style="height: 24px; width: 3px; background:${seriesPerYear[0].color}; margin-right: 10px;"></div>
                                            <span style="color: #111827;font-weight:bold;font-size:14px;margin-right: 30px;">Estimated data</span><span style="font-size:14px;font-weight:bold;color:#000;margin-left:auto;">${estimatedSerie?.tooltip.estimated_percentage ?? '0'}<span style="font-weight: medium;">%</span></span>
                                        </div><br>
                                    `;
                                }
                                total += parseFloat(itemValue);
                            }
                        });
                        tooltipContent += `
                            <div style="display:inline-flex; align-items: center; justify-content: between; width: 100%;margin-top: -8px;">
                                <div style="height: 28px; width: 3px; border-bottom-left-radius: 10px;border-bottom-right-radius: 10px; background:${seriesPerYear[0].color}; margin-right: 10px;"></div>
                                <span style="color: #111827;font-weight:bold;font-size:14px;margin-right: 30px;">Total</span><span style="font-size:14px;font-weight:bold;color:#000;margin-left:auto;">${Math.round(total * 100) / 100}</span>
                            </div><br>
                        `;
                    }
                })
                return tooltipContent;
            },
        };
        chartInstance.setOption({ tooltip: tooltipOptions });
    }
}
